import { computed, inject, Injectable, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { AppError } from '@/app/core/http/app-error.model';
import { toAppError } from '@/app/core/http/error-catalog';
import { DEFAULT_PAGE_SIZE, PageMeta } from '@/app/core/models/paged-result.model';
import { Payroll, PayrollFilters } from '../models/payroll.model';
import { PayrollRepository } from '../services/payroll.repository';

export type PayrollsStatus = 'loading' | 'ready' | 'empty' | 'error';

const ERROR_SUBJECT = 'las nóminas';

const emptyPage = (): PageMeta => ({
  page: 0,
  size: DEFAULT_PAGE_SIZE,
  totalElements: 0,
  totalPages: 1,
});

@Injectable()
export class PayrollsStore {
  private readonly repository = inject(PayrollRepository);

  private readonly _status = signal<PayrollsStatus>('loading');
  private readonly _records = signal<readonly Payroll[]>([]);
  private readonly _pageMeta = signal<PageMeta>(emptyPage());
  private readonly _filters = signal<PayrollFilters>({});
  private readonly _size = signal(DEFAULT_PAGE_SIZE);
  private readonly _error = signal<AppError | null>(null);
  private inFlight?: Subscription;

  readonly status = this._status.asReadonly();
  readonly records = this._records.asReadonly();
  readonly pageMeta = this._pageMeta.asReadonly();
  readonly filters = this._filters.asReadonly();
  readonly error = this._error.asReadonly();

  readonly isLoading = computed(() => this._status() === 'loading');

  // El store vive en la ruta: sin esto, al volver a la página se repetiría el último error o resultado.
  reset(): void {
    this.inFlight?.unsubscribe();
    this._filters.set({});
    this._size.set(DEFAULT_PAGE_SIZE);
    this._records.set([]);
    this._pageMeta.set(emptyPage());
    this._error.set(null);
    this._status.set('loading');
  }

  search(filters: PayrollFilters): void {
    this._filters.set(filters);
    this.load(0);
  }

  changeSize(size: number): void {
    this._size.set(size);
    this.load(0);
  }

  load(page = 0): void {
    this.inFlight?.unsubscribe();
    this._error.set(null);
    this._status.set('loading');

    this.inFlight = this.repository
      .search(this._filters(), { page, size: this._size() })
      .subscribe({
        next: (result) => {
          this._records.set(result.content);
          this._pageMeta.set(result.page);
          this._status.set(result.page.totalElements === 0 ? 'empty' : 'ready');
        },
        error: (cause: unknown) => {
          this._records.set([]);
          this._pageMeta.set(emptyPage());
          this._error.set(toAppError(cause, ERROR_SUBJECT));
          this._status.set('error');
        },
      });
  }
}
